import { Blossom } from "@blossom-carousel/core";
import { useCallback, useEffect, useRef } from "react";
import "@blossom-carousel/core/style.css";
import { TabList } from "../core/react-aria/Tabs";

/** Subtrees where Blossom must not capture drag (e.g. nested sliders). Use as a data attribute. */
export const BLOSSOM_NO_DRAG = "data-blossom-no-drag";

export interface BlossomCarouselProps extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode;
  repeat?: boolean;
  ref?: React.Ref<HTMLElement>;
  as?: "list" | "tabs";
}

function isInsideNoDragRegion(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(`[${BLOSSOM_NO_DRAG}]`) != null;
}

/**
 * Blossom registers `pointerdown` / `wheel` on the scroller. `stopImmediatePropagation()` on that
 * same node can prevent React’s delegated `pointerdown` from running, so `useMove` never wires
 * `pointermove` on `window` — the handle only updates on release. Wrapping `addEventListener` skips
 * Blossom’s handler when the target is inside `[data-blossom-no-drag]` without touching the event
 * path for other listeners (including React on the app root).
 */
function patchScrollerPointerAndWheel(el: HTMLElement): () => void {
  const origAdd = el.addEventListener.bind(el);
  const origRemove = el.removeEventListener.bind(el);
  const wrappedByOriginal = new WeakMap<EventListener, EventListener>();

  el.addEventListener = function (
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ) {
    if ((type === "pointerdown" || type === "wheel") && typeof listener === "function") {
      const wrapped: EventListener = e => {
        if (isInsideNoDragRegion(e.target)) return;
        listener.call(el, e);
      };
      wrappedByOriginal.set(listener, wrapped);
      return origAdd(type, wrapped, options);
    }
    return origAdd(type, listener, options);
  };

  el.removeEventListener = function (
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions,
  ) {
    if ((type === "pointerdown" || type === "wheel") && typeof listener === "function") {
      const wrapped = wrappedByOriginal.get(listener);
      if (wrapped) {
        wrappedByOriginal.delete(listener);
        return origRemove(type, wrapped, options);
      }
    }
    return origRemove(type, listener, options);
  };

  return () => {
    el.addEventListener = origAdd;
    el.removeEventListener = origRemove;
  };
}

// Adapted from `@blossom-carousel/react` for compatibility with `@base-ui/react/scroll-area`
export function BlossomCarousel({ children, repeat = false, ref, as = "list", ...props }: BlossomCarouselProps) {
  const blossomRef = useRef<ReturnType<typeof Blossom> | null>(null);
  const internalRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!internalRef.current) return;

    const el = internalRef.current;
    const restoreAddRemove = patchScrollerPointerAndWheel(el);

    const blossom = Blossom(el, { repeat });
    blossomRef.current = blossom;

    blossom.init();

    return () => {
      blossom.destroy();
      restoreAddRemove();
    };
  }, [repeat]);

  const mergedRef = useCallback(
    (node: HTMLElement | null) => {
      internalRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  const List = as === "tabs" ? TabList : "ul";

  return (
    <List ref={mergedRef} role={as === "list" ? "list" : undefined} blossom-carousel='true' {...props}>
      {children}
    </List>
  );
}
